import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Cancel,
  CancelScheduleSend,
  DoneOutline,
  ErrorOutline,
  ExpandLess,
  ExpandMore,
  QuestionAnswer,
  Search,
  Send
} from '@mui/icons-material';
import {
  Box,
  Avatar,
  Chip,
  Stack,
  Tooltip,
  Typography,
  Badge,
  AlertColor
} from '@mui/material';
import {
  getGridSingleSelectOperators,
  GridEventListener,
  GridFilterInputValueProps,
  GridFilterModel,
  GridRenderCellParams,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
  GridValueFormatterParams
} from '@mui/x-data-grid';
import { DataGridProProps, useGridApiRef } from '@mui/x-data-grid-pro';

import { useQueryClient } from '@tanstack/react-query';
import { makeStyles } from 'tss-react/mui';

import Page from '@/components/Page';
import UpdatesDialog from '@/components/Updates';
import { violationsConfig } from '@/components/Updates/config/config';
import Alert from '@/components/alert';
import DataTable from '@/components/dataTable';
import ChipsColumn from '@/components/dataTable/customChipsColumn';
import FilterCreators from '@/components/dataTable/customFilters/FilterCreators';
import FilterEvaluations from '@/components/dataTable/customFilters/FilterEvaluations';
import NoRowsOverlay from '@/components/dataTable/customNoRowsOverlay/NoRowsOverlay';
import OnBoardingOverlay from '@/components/dataTable/customNoRowsOverlay/OnBoardingOverlay';
import creatorAndGroupsOperator from '@/components/dataTable/customOperators/creatorAndGroupsOperator';
import dateRangeOperator from '@/components/dataTable/customOperators/dateRangeOperator';
import evaluationOperator from '@/components/dataTable/customOperators/evaluationOperator';
import Header from '@/components/header';
import UpdatesIcon from '@/components/icons/UpdatesIcon';
import { ViolationSteps } from '@/constants/driverSteps';
import PLATFORM_MAPPER from '@/constants/platformMapper';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@/constants/table';
import { useViolations } from '@/hooks/queries/violations';
import { useAppSelector } from '@/hooks/redux';
import useDriver from '@/hooks/useDriver';
import useTableParams from '@/hooks/useTableParams';
import useWorkspaceNavigate from '@/hooks/useWorkspaceNavigate';
import { CreatorsAutocomplete, CreatorGroups } from '@/models/creator';
import { Labels } from '@/models/labels';
import { Platform } from '@/models/posts';
import { Updates } from '@/models/updates';
import { Violations } from '@/models/violations';
import theme from '@/themes/theme';
import { formatDate } from '@/utils/datesAndTime';
import { getMonthsSince } from '@/utils/datesAndTime/datesAndTime';
import getRoute from '@/utils/getRoute';
import PreviewColumn from '@/views/evaluations/detail/body/_partials/PreviewColumn';
import Error from '@/views/misc/error';

import DetailPanel from './_partials/DetailPanel';
import ViolationsStatistics from './_partials/ViolationsStatistics';

type AutocompleteOption = CreatorsAutocomplete.Creator | CreatorGroups.Group;

const getInitialFilterModelItems = (params: Record<string, any>) => {
  const {
    creator = [],
    status,
    contacted,
    uploadDate,
    secondOpinion,
    platform,
    evaluationId
  } = params;

  const items = [];

  if (creator?.length > 0) {
    items.push({
      field: 'creator',
      id: items.length,
      operator: 'isAnyOf',
      value: creator
    });
  }
  if (status !== undefined) {
    items.push({
      field: 'status',
      id: items.length,
      operator: 'isAnyOf',
      value: status.split(',')
    });
  }
  if (contacted !== undefined)
    items.push({
      field: 'contacted',
      id: items.length,
      operator: 'is',
      value: contacted
    });
  if (uploadDate !== undefined)
    items.push({
      field: 'uploadDate',
      id: items.length,
      operator: 'between',
      value: uploadDate.split(',').map((item: string) => new Date(item))
    });
  if (secondOpinion !== undefined)
    items.push({
      field: 'secondOpinion',
      id: items.length,
      operator: 'is',
      value: secondOpinion
    });
  if (platform !== undefined)
    items.push({
      field: 'platform',
      id: items.length,
      operator: 'isAnyOf',
      value: platform.split(',')
    });
  if (evaluationId !== undefined)
    items.push({
      field: 'evaluationId',
      id: items.length,
      operator: 'is',
      value: evaluationId
    });
  return items;
};

const ViolationsOnBoardingOverlay = () => (
  <OnBoardingOverlay
    icon={<ErrorOutline color="action" sx={{ width: 40, height: 40 }} />}
    title="Start reviewing Violations"
    text="Create an evaluation to start analysing social media posts for violations"
  />
);

const secondOpinionOptions: Record<string, string> = {
  all: 'All',
  true: 'Agree',
  false: 'Disagree',
  any: 'Agree & Disagree',
  none: 'No opinion'
};

const defaultParams = {
  page: DEFAULT_PAGE,
  size: DEFAULT_PAGE_SIZE,
  sort: undefined,
  order: undefined,
  name: undefined,
  status: undefined,
  contacted: undefined,
  uploadDate: undefined,
  secondOpinion: undefined,
  platform: undefined,
  creator: undefined,
  evaluationId: undefined
};

const useStyles = makeStyles()((theme) => ({
  table: {
    '& .MuiDataGrid-pinnedColumnHeaders, & .MuiDataGrid-pinnedColumns': {
      backgroundColor: theme.palette.common.white
    },
    '& .MuiDataGrid-main': {
      containerType: 'inline-size'
    },
    '& .MuiDataGrid-detailPanel': {
      overflow: 'visible'
    },
    cursor: 'pointer'
  }
}));

const ViolationsList = () => {
  const apiRef = useGridApiRef();
  const queryClient = useQueryClient();
  const navigate = useWorkspaceNavigate();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedViolation, setSelectedViolation] = useState<Updates.Dialog>();
  const { params, handlers, updateParams, paramsInTheUrl } = useTableParams(
    getRoute.violations.LIST(),
    defaultParams
  );
  const { classes } = useStyles();
  const user = useAppSelector((state) => state.user.currentUser);

  const {
    data: violations,
    isFetching,
    error
  } = useViolations({
    params: {
      ...params
    }
  });

  const violation = violations?.results.find(
    (v) => v.postId === selectedViolation?.postId
  );

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['violations'] });
    const initialValue = !params.status && !paramsInTheUrl.statusCleared;

    if (initialValue) {
      updateParams({
        status: 'open,other'
      });
    }
  }, []);

  const initialFilterModel = useRef({
    items: getInitialFilterModelItems({
      ...params,
      status: paramsInTheUrl.statusCleared
        ? undefined
        : params.status ?? 'open,other'
    })
  });

  const onRowClick = useCallback<GridEventListener<'rowClick'>>(
    (params) => {
      apiRef.current.toggleDetailPanel(params.id);
    },
    [apiRef]
  );
  const handleReviewClick = useCallback(
    (postId: number): void => {
      const newsParams = { ...params };
      delete newsParams.page;
      delete newsParams.size;
      queryClient.refetchQueries({ queryKey: ['violation'] });
      navigate(getRoute.violations.DETAIL(postId, newsParams));
    },
    [navigate]
  );

  const getDetailPanelContent = useCallback<
    NonNullable<DataGridProProps['getDetailPanelContent']>
  >(
    ({ row }) => <DetailPanel row={row} onReviewClick={handleReviewClick} />,
    [handleReviewClick]
  );

  const getDetailPanelHeight = useCallback<
    NonNullable<DataGridProProps['getDetailPanelHeight']>
  >(() => 'auto' as const, []);

  const columns = useMemo(
    () => [
      {
        filterable: false,
        sortable: false,
        field: 'previewUrl',
        headerName: 'Preview',
        minWidth: 130,
        flex: 1,
        renderCell: (
          params: GridRenderCellParams<Violations.ViolationRecord>
        ) => (
          <PreviewColumn
            previewUrl={params.row.previewUrl}
            isExpired={params.row.isExpired}
          />
        )
      },
      {
        sortable: true,
        field: 'creator',
        headerName: 'Creator',
        minWidth: 200,
        flex: 1,
        type: 'singleSelect',
        filterOperators: creatorAndGroupsOperator.map((operator) => ({
          ...operator,
          InputComponent: (props: GridFilterInputValueProps) => (
            <FilterCreators {...props} showGroups={false} violations />
          )
        })),
        valueFormatter: (
          params: GridValueFormatterParams<Violations.Creator>
        ) => params.value.name || '—',
        renderCell: (
          data: GridRenderCellParams<Violations.ViolationRecord>
        ) => {
          const { creator } = data.row;

          return (
            <Stack
              direction="row"
              alignItems="center"
              overflow="hidden"
              gap={1}
            >
              <Avatar
                src={creator.avatarUrl || ''}
                alt={creator.name}
                sx={{ width: 40, height: 40, fontSize: 16 }}
              />
              <Typography
                noWrap
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flexShrink: 1,
                  minWidth: 0
                }}
              >
                {creator.name}
              </Typography>
            </Stack>
          );
        }
      },
      {
        field: 'uploadDate',
        headerName: 'Posting Time',
        minWidth: 160,
        type: 'date',
        filterOperators: dateRangeOperator,
        valueFormatter: (
          params: GridValueFormatterParams<Violations.ViolationRecord>
        ) => String(params.value),
        flex: 1,
        renderCell: (
          data: GridRenderCellParams<Violations.ViolationRecord>
        ) => {
          const { uploadDate } = data.row;

          return (
            <Typography>
              {formatDate(new Date(uploadDate), 'dd/MM/yyyy HH:mm')}
            </Typography>
          );
        }
      },
      {
        sortable: false,
        field: 'platform',
        headerName: 'Platform',
        type: 'singleSelect',
        valueOptions: Object.keys(PLATFORM_MAPPER).map((key) => ({
          value: key,
          label: PLATFORM_MAPPER[key as Platform]
        })),
        minWidth: 150,
        filterOperators: getGridSingleSelectOperators().filter(
          (operator) => operator.value === 'isAnyOf'
        ),
        flex: 1,
        renderCell: (
          params: GridRenderCellParams<Violations.ViolationRecord>
        ) => <Chip label={PLATFORM_MAPPER[params.row.platform as Platform]} />
      },
      {
        sortable: false,
        field: 'secondOpinion',
        headerName: 'Second Opinion',
        minWidth: 120,
        type: 'singleSelect',
        valueOptions: Object.keys(secondOpinionOptions).map((key) => ({
          value: key,
          label: secondOpinionOptions[key as string]
        })),
        filterOperators: getGridSingleSelectOperators().filter(
          (operator) => operator.value === 'is'
        ),
        valueFormatter: (
          params: GridValueFormatterParams<Violations.SecondOpinion>
        ) => params.value.opinion || '—',
        flex: 1,
        renderCell: (
          data: GridRenderCellParams<Violations.ViolationRecord>
        ) => {
          const { secondOpinion } = data.row;

          if (!secondOpinion || Object.keys(secondOpinion).length === 0) {
            return <>—</>;
          }

          return (
            <Tooltip title={secondOpinion.author} arrow placement="top">
              {secondOpinion.opinion ? (
                <Typography fontWeight={700} color="primary" variant="body2">
                  Agree
                </Typography>
              ) : (
                <Typography fontWeight={700} color="error" variant="body2">
                  Disagree
                </Typography>
              )}
            </Tooltip>
          );
        }
      },
      {
        sortable: false,
        type: 'boolean',
        field: 'contacted',
        headerName: 'Contacted',
        valueFormatter: (
          params: GridValueFormatterParams<Violations.Contacted>
        ) => params.value.contactedAt || '—',
        minWidth: 130,
        flex: 1,
        renderCell: (
          data: GridRenderCellParams<Violations.ViolationRecord>
        ) => {
          const { contacted } = data.row;

          if (contacted.contactedAt && contacted.author) {
            return (
              <Tooltip
                arrow
                title={
                  <Stack>
                    <Typography
                      variant="caption"
                      textAlign="center"
                      fontWeight="bold"
                    >
                      Last contacted:
                    </Typography>
                    <Typography variant="caption" textAlign="center">
                      {formatDate(
                        new Date(contacted.contactedAt),
                        'dd/MM/yyyy HH:mm'
                      )}
                    </Typography>
                    <Typography
                      variant="caption"
                      textAlign="center"
                      fontWeight="bold"
                    >{`@${contacted.author}`}</Typography>
                  </Stack>
                }
                placement="top"
              >
                <Chip
                  color="success"
                  icon={<Send fontSize="inherit" />}
                  label="Contacted"
                />
              </Tooltip>
            );
          }

          return (
            <Chip
              color="error"
              icon={<CancelScheduleSend fontSize="inherit" />}
              label="No contact"
            />
          );
        }
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'singleSelect',
        valueOptions: [
          { value: 'open', label: 'Open' },
          { value: 'closed', label: 'Closed' },
          { value: 'other', label: 'Other' }
        ],
        valueFormatter: (params: GridValueFormatterParams<Violations.Status>) =>
          params.value.stage || '—',
        filterOperators: getGridSingleSelectOperators().filter(
          (operator) => operator.value === 'isAnyOf'
        ),
        minWidth: 130,
        flex: 1,
        renderCell: (
          data: GridRenderCellParams<Violations.ViolationRecord>
        ) => {
          const { status } = data.row;

          switch (status.stage) {
            case 'open':
              return (
                <Tooltip
                  title={
                    <Typography variant="caption">
                      <strong>Opened at:</strong>{' '}
                      {formatDate(status.openedAt, 'dd/MM/yyyy HH:mm')}
                    </Typography>
                  }
                  arrow
                  placement="top"
                >
                  <Chip
                    icon={<Search fontSize="small" />}
                    label="Open"
                    color="warning"
                  />
                </Tooltip>
              );

            case 'closed':
              return (
                <Tooltip
                  title={
                    <Typography variant="caption">
                      <strong>Closed at:</strong>{' '}
                      {formatDate(status.updatedAt, 'dd/MM/yyyy HH:mm')}
                    </Typography>
                  }
                  arrow
                  placement="top"
                >
                  <Chip
                    icon={<DoneOutline fontSize="small" />}
                    label="Closed"
                    color="success"
                  />
                </Tooltip>
              );

            case 'other':
              return (
                <Tooltip title={status.comment} arrow placement="top">
                  <Chip
                    icon={<QuestionAnswer fontSize="small" />}
                    label="Other"
                    color="default"
                  />
                </Tooltip>
              );

            default:
              return (
                <Chip
                  icon={<Cancel fontSize="small" />}
                  label="Unknown"
                  color="default"
                />
              );
          }
        }
      },
      {
        field: 'labels',
        headerName: 'Labels',
        filterable: false,
        minWidth: 130,
        flex: 1,
        sortable: false,
        valueFormatter: (params: GridValueFormatterParams<Labels.Details>) =>
          params.value.name || '—',
        renderCell: (
          data: GridRenderCellParams<Violations.ViolationRecord>
        ) => {
          const { labels } = data.row;
          return (
            <ChipsColumn
              data={labels.map((label) => ({
                keyword: label.name,
                tooltip: label.category?.name ?? ''
              }))}
              chipColor="default"
              cellWidth={data.colDef.computedWidth}
            />
          );
        }
      },
      {
        field: 'evaluationId',
        headerName: 'Evaluation',
        minWidth: 210,
        type: 'singleSelect',
        sortable: false,
        flex: 1,
        filterOperators: evaluationOperator.map((operator) => ({
          ...operator,
          InputComponent: (props: GridFilterInputValueProps) => (
            <FilterEvaluations {...props} />
          )
        })),
        renderCell: (
          params: GridRenderCellParams<Violations.ViolationRecord>
        ) => {
          const { violations } = params.row;

          return (
            <Stack direction="column">
              {violations.map(
                (violation: Violations.Violation, idx: number) => (
                  <Typography key={idx} whiteSpace="normal" variant="body2">
                    {violation.evaluationName}
                  </Typography>
                )
              )}
            </Stack>
          );
        }
      },
      {
        field: 'changes',
        headerName: 'Changes',
        minWidth: 80,
        type: 'boolean',
        filterable: false,
        sortable: false,
        flex: 1,
        renderCell: (
          params: GridRenderCellParams<Violations.ViolationRecord>
        ) => {
          const { updates } = params.row;
          const rowIndex = params.api.getRowIndexRelativeToVisibleRows(
            params.id
          );
          const updatesCount = updates?.length || 0;
          const currentRow = violations?.results[rowIndex || 0];
          const creatorName = currentRow?.creator?.name || '';

          return (
            <Box
              id="violation-updates"
              onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                setIsModalOpen(true);
                setSelectedViolation({
                  postId: params.row.postId,
                  evaluationId: params.row.violations[0].evaluationId,
                  creatorName,
                  updates
                });
                e.stopPropagation();
              }}
              position="relative"
              display="inline-block"
              sx={{
                backgroundColor: theme.palette.common.black,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'scale(1.1)'
                }
              }}
            >
              <Badge
                badgeContent={updatesCount}
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: 'secondary.main',
                    color: 'common.white'
                  }
                }}
              >
                <Stack
                  width={30}
                  height={30}
                  alignItems="center"
                  justifyContent="center"
                  direction="column"
                >
                  <UpdatesIcon fill={theme.palette.common.white} />
                </Stack>
              </Badge>
            </Box>
          );
        }
      }
    ],
    [violations]
  );

  const rows = useMemo(
    () =>
      violations?.results.map((item: Violations.ViolationRecord) => ({
        ...item,
        id: item.postId
      })),
    [violations]
  );

  const onFilterModelChange = useCallback(
    (model: GridFilterModel) => {
      const newParams: Record<string, any> = {};

      // This loop now correctly handles adding and removing filters
      model.items.forEach((item) => {
        const { field, value } = item;

        if (field === 'creator' && Array.isArray(value)) {
          if (value.length === 0) {
            newParams.creator = undefined;
            newParams.creatorsGroup = undefined;
            return; // Continue to the next filter item
          }

          // This existing logic is still correct for when there are selections
          const creators: string[] = [];
          const creatorGroups: string[] = [];

          value.forEach((option: AutocompleteOption) => {
            if (option.type === 'Creators') {
              creators.push(String(option.id));
            } else if (option.type === 'Groups') {
              creatorGroups.push(String(option.id));
            }
          });

          newParams.creator =
            creators.length > 0 ? creators.join(',') : undefined;
          newParams.creatorsGroup =
            creatorGroups.length > 0 ? creatorGroups.join(',') : undefined;
        } else if (field === 'status' && Array.isArray(value)) {
          if (value.length === 0) {
            newParams.status = undefined;
            newParams.statusCleared = true;
            return;
          }
          newParams.status = value.join(',');
          newParams.statusCleared = undefined;
        } else if (field === 'evaluationId' && value) {
          const id = typeof value === 'object' ? value.id : value;
          newParams.evaluationId = id ? String(id) : undefined;
        } else if (field === 'uploadDate' && value?.length > 0) {
          const formattedDates: string[] = value
            .map((date: Date) => formatDate(date, 'yyyy-MM-dd'))
            .filter(Boolean);

          newParams.uploadDate = formattedDates.length
            ? formattedDates.join(',')
            : undefined;
        } else if (field === 'platform' && Array.isArray(value)) {
          const validPlatforms = value.filter(
            (v) => v && typeof v === 'string'
          );
          newParams.platform =
            validPlatforms.length > 0 ? validPlatforms.join(',') : undefined;
        } else if (value && value.length > 0) {
          // (No changes needed here, logic is correct)
          newParams[field] = Array.isArray(value) ? value.join(',') : value;
        }
      });

      // This loop is still needed to handle removing filter blocks entirely (e.g., "Clear All")
      columns
        .filter((column) => column.field)
        .forEach((column) => {
          const isFilterActive = model.items.some(
            (item) => item.field === column.field
          );

          if (!isFilterActive && paramsInTheUrl[column.field]) {
            if (column.field === 'status') {
              newParams.statusCleared = true;
              newParams.status = undefined;
            }
            if (column.field === 'creator') {
              newParams.creator = undefined;
              newParams.creatorsGroup = undefined;
            } else if (column.field === 'evaluationId') {
              newParams.evaluationId = undefined;
            } else {
              newParams[column.field] = undefined;
            }
          }

          // Clear the marker if status filter is added back
          if (column.field === 'status' && isFilterActive) {
            const statusItem = model.items.find(
              (item) => item.field === 'status'
            );
            const hasValues =
              statusItem &&
              Array.isArray(statusItem.value) &&
              statusItem.value.length > 0;

            // Only clear statusCleared if the filter actually has values
            if (hasValues && newParams.statusCleared === undefined) {
              // Don't override if first loop already set it
              newParams.statusCleared = undefined;
            }
          }
        });

      updateParams({
        ...paramsInTheUrl,
        ...newParams,
        page: DEFAULT_PAGE
      });
    },
    [columns, paramsInTheUrl, updateParams]
  );

  let emptyStateViolations = ViolationsOnBoardingOverlay;
  if (
    rows?.length === 0 &&
    Object.entries(paramsInTheUrl)
      .filter(([key]) => key !== 'page' && key !== 'size')
      .some(([, v]) => v !== undefined)
  ) {
    emptyStateViolations = () => <NoRowsOverlay />;
  }

  useDriver({
    isLoading: isFetching,
    steps: ViolationSteps
  });

  const getTermsAlert = useCallback((): {
    severity: AlertColor | undefined;
    message: string;
    action: string;
  } => {
    const isExpired = violations?.results.some((result) => result.isExpired);
    const allExpired =
      !!violations?.results.length &&
      violations.results.every((result) => result.isExpired);

    const violationTerm = new Date(user.workspaceStorageTerms.violation);
    const deletionTerms = `Violation term: ${getMonthsSince(
      violationTerm
    )} months.`;

    if (!isExpired) return { severity: undefined, action: '', message: '' };

    if (allExpired) {
      return {
        severity: 'error',
        action: deletionTerms,
        message:
          'All posts in this evaluation have expired according to the data storage terms.'
      };
    }

    return {
      severity: 'warning',
      message:
        'Some violations have expired according to the data storage term.',
      action: deletionTerms
    };
  }, [violations]);

  if (error) return <Error message="Failed to load violations" error={error} />;

  const termsAlert = getTermsAlert();

  return (
    <Page title="Violations" header={<Header title="Violations" />}>
      <Stack gap={3}>
        <ViolationsStatistics />
        {termsAlert.message && (
          <Alert
            sx={{ alignItems: 'center' }}
            severity={termsAlert.severity}
            content={termsAlert.message}
            action={termsAlert.action}
          />
        )}
        <Box id="violation-item">
          <DataTable
            apiRef={apiRef}
            className={classes.table}
            columns={columns}
            rowHeight={72}
            loading={isFetching}
            rows={rows || []}
            filterMode="server"
            paginationMode="server"
            sortingMode="server"
            onChangePageSize={handlers.onChangePageSize}
            onChangePage={handlers.onChangePage}
            onSortModelChange={handlers.onSortModelChange}
            onFilterModelChange={onFilterModelChange}
            getDetailPanelContent={getDetailPanelContent}
            getDetailPanelHeight={getDetailPanelHeight}
            initialState={{
              pinnedColumns: {
                right: ['changes']
              },
              filter: {
                filterModel: initialFilterModel.current
              }
            }}
            slots={{
              detailPanelExpandIcon: ExpandMore,
              detailPanelCollapseIcon: ExpandLess
            }}
            noRowsOverlay={emptyStateViolations}
            toolbar={() => (
              <GridToolbarContainer
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px 0 12px',
                  height: '48px'
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  flexGrow={1}
                  sx={{
                    '& .MuiButton-root': {
                      color: theme.palette.text.primary
                    },
                    '& .MuiSvgIcon-root': {
                      color: theme.palette.text.primary
                    }
                  }}
                >
                  <GridToolbarColumnsButton />
                  <GridToolbarFilterButton
                    componentsProps={{
                      button: {
                        id: 'violation-filter'
                      }
                    }}
                  />
                  <GridToolbarDensitySelector />
                </Stack>
              </GridToolbarContainer>
            )}
            onRowClick={onRowClick}
            rowCount={violations?.totalItems}
            sort={params.sort}
            order={params.order}
            page={params.page}
            size={params.size}
          />
        </Box>
        {selectedViolation && (
          <UpdatesDialog
            isOpen={isModalOpen}
            context={selectedViolation}
            updates={violation}
            onClose={() => setIsModalOpen(false)}
            config={violationsConfig}
          />
        )}
      </Stack>
    </Page>
  );
};

export default ViolationsList;
